import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Row, Form } from 'react-bootstrap';
import { InputBox } from '../form-elements/inputbox.component';
import { DropdownMenu } from '../form-elements/dropdownmenu.component';
import { sellerTypes } from './values';
import { InputBoxPhone } from '../form-elements/inputbox-phone.component';
import { InputBoxNumber } from '../form-elements/inputbox-number.component';
import { InputBoxMoney } from '../form-elements/inputbox-money.component';
import { CompanyConfig } from '../../company.config';

interface SaleDetailsFormProps {
  values?: any;
  errors?: any;
  touched?: any;
  handleChange?: any;
  handleBlur?: any;
  setFieldValue?: any;
}

export const initialValuesSaleDetails = {
  sellerType: '',
  sellerName: '',
  sellerPhoneNumber: '',
  sellerContact: '',
  dealerName: '',
  // collateralType / unitStatus are only used by TSF (not CCL)
  collateralType: '',
  unitStatus: '',
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleSalesPrice: '',
  vehicleDownPayment: '',
  notes: '',
  forDailyDriver: '',
  referralSource: '',
  referralSourceOther: '',
};

export const SaleDetailsForm: React.FC<SaleDetailsFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const { dealerId } = useParams();

  useEffect(() => {
    if (values.sellerType.toLowerCase() !== 'private party') {
      setFieldValue('sellerName', initialValuesSaleDetails.sellerName);
    } else if (values.sellerType.toLowerCase() !== 'dealer') {
      setFieldValue('sellerContact', initialValuesSaleDetails.sellerContact);
      setFieldValue('dealerName', initialValuesSaleDetails.dealerName);
    }
  }, [setFieldValue, values.sellerType]);

  useEffect(() => {
    if (dealerId) {
      setFieldValue('sellerType', 'Dealer');
      // Default referralSource to "Dealership" when dealerId exists and value is empty
      if (!values.referralSource || values.referralSource === '') {
        setFieldValue('referralSource', 'Dealership');
      }
    }
  }, [dealerId, setFieldValue]);

  useEffect(() => {
    if (values.referralSource !== 'Other') {
      setFieldValue('referralSourceOther', initialValuesSaleDetails.referralSourceOther);
    }
  }, [values.referralSource, setFieldValue]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <h3>Purchase Information</h3>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={6}>
          <DropdownMenu
            label="Seller Type"
            name="sellerType"
            options={sellerTypes}
            value={values.sellerType}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.sellerType && !errors.sellerType}
            isInvalid={touched.sellerType && errors.sellerType}
            errorFeedback={errors.sellerType}
            disabled={!!dealerId}
          />
        </Col>

        {values.sellerType !== '' ? (
          <Col lg="6">
            <InputBoxPhone
              label="Phone Number"
              name="sellerPhoneNumber"
              value={values.sellerPhoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              setFieldValue={setFieldValue}
              isValid={touched.sellerPhoneNumber && !errors.sellerPhoneNumber}
              isInvalid={touched.sellerPhoneNumber && errors.sellerPhoneNumber}
              errorFeedback={errors.sellerPhoneNumber}
              disabled={!!dealerId}
            />
          </Col>
        ) : null}

        {values.sellerType === 'Private Party' ? (
          <Col xs={12} md={6}>
            <InputBox
              label="Seller Name"
              type="text"
              name="sellerName"
              value={values.sellerName}
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.sellerName && !errors.sellerName}
              isInvalid={touched.sellerName && errors.sellerName}
              errorFeedback={errors.sellerName}
            />
          </Col>
        ) : null}

        {values.sellerType === 'Dealer' ? (
          <>
            <Col xs={12} md={6}>
              <InputBox
                label="Dealer Name"
                type="text"
                name="dealerName"
                value={values.dealerName}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.dealerName && !errors.dealerName}
                isInvalid={touched.dealerName && errors.dealerName}
                errorFeedback={errors.dealerName}
                disabled={!!dealerId}
              />
            </Col>
            <Col xs={12} md={6}>
              <InputBox
                label="Salesperson/Contact"
                type="text"
                name="sellerContact"
                value={values.sellerContact}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.sellerContact && !errors.sellerContact}
                isInvalid={touched.sellerContact && errors.sellerContact}
                errorFeedback={errors.sellerContact}
              />
            </Col>
          </>
        ) : null}
      </Row>

      <Row>
        <Col xs={12} md={4}>
          <InputBoxNumber
            label="Year"
            name="vehicleYear"
            value={values.vehicleYear}
            onChange={handleChange}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            isValid={touched.vehicleYear && !errors.vehicleYear}
            isInvalid={touched.vehicleYear && errors.vehicleYear}
            errorFeedback={errors.vehicleYear}
          />
        </Col>
        <Col xs={12} md={4}>
          <InputBox
            label="Make"
            type="text"
            name="vehicleMake"
            value={values.vehicleMake}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.vehicleMake && !errors.vehicleMake}
            isInvalid={touched.vehicleMake && errors.vehicleMake}
            errorFeedback={errors.vehicleMake}
          />
        </Col>
        <Col xs={12} md={4}>
          <InputBox
            label="Model"
            name="vehicleModel"
            value={values.vehicleModel}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.vehicleModel && !errors.vehicleModel}
            isInvalid={touched.vehicleModel && errors.vehicleModel}
            errorFeedback={errors.vehicleModel}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={6}>
          <InputBoxMoney
            label="Sales Price"
            name="vehicleSalesPrice"
            value={values.vehicleSalesPrice}
            onChange={handleChange}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            isValid={touched.vehicleSalesPrice && !errors.vehicleSalesPrice}
            isInvalid={touched.vehicleSalesPrice && errors.vehicleSalesPrice}
            errorFeedback={errors.vehicleSalesPrice}
          />
        </Col>
        <Col xs={12} md={6}>
          <InputBoxMoney
            label="Down Payment"
            name="vehicleDownPayment"
            value={values.vehicleDownPayment}
            onChange={handleChange}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            isValid={touched.vehicleDownPayment && !errors.vehicleDownPayment}
            isInvalid={touched.vehicleDownPayment && errors.vehicleDownPayment}
            errorFeedback={errors.vehicleDownPayment}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Form.Group>
            <Form.Label className="me-2">
              Is this vehicle being purchased with the intent to be used as a daily driver?
            </Form.Label>
            <Form.Check
              inline
              type="radio"
              name="forDailyDriver"
              value="true"
              id="forDailyDriver-yes"
              label="Yes"
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.forDailyDriver && !errors.forDailyDriver}
              isInvalid={touched.forDailyDriver && errors.forDailyDriver}
            />
            <Form.Check
              inline
              type="radio"
              name="forDailyDriver"
              value="false"
              id="forDailyDriver-no"
              label="No"
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.forDailyDriver && !errors.forDailyDriver}
              isInvalid={touched.forDailyDriver && errors.forDailyDriver}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <h3>Additional Information</h3>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <InputBox
            label="Notes"
            name="notes"
            value={values.notes}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.notes && !errors.notes}
            isInvalid={touched.notes && errors.notes}
            errorFeedback={errors.notes}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={6}>
          <DropdownMenu
            label="How did you hear about us?"
            name="referralSource"
            options={CompanyConfig.referralSources}
            value={values.referralSource}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched.referralSource && !errors.referralSource}
            isInvalid={touched.referralSource && errors.referralSource}
            errorFeedback={errors.referralSource}
          />
        </Col>
        {values.referralSource === 'Other' ? (
          <Col xs={12} md={6}>
            <InputBox
              label="Other"
              name="referralSourceOther"
              value={values.referralSourceOther}
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.referralSourceOther && !errors.referralSourceOther}
              isInvalid={touched.referralSourceOther && errors.referralSourceOther}
              errorFeedback={errors.referralSourceOther}
            />
          </Col>
        ) : null}
      </Row>
    </>
  );
};
